import type { TFunction } from 'i18next';
import type { TimelineEvent, TimelineEventType } from '../../types/timeline';
import { icpAnalysesPath } from '../../lib/icpRoutes';

/** Translation key of each event type's label (the dashboard track's wording). */
export const TIMELINE_TYPE_LABEL_KEY: Record<TimelineEventType, string> = {
  journal_entry: 'dashboard.timeline.type.journal',
  image: 'dashboard.timeline.type.image',
  measurement: 'dashboard.timeline.type.measurement',
  icp_analysis: 'dashboard.timeline.type.icpAnalysis',
  dosing: 'dashboard.timeline.type.dosing',
  livestock: 'dashboard.timeline.type.livestockAdded',
  task: 'dashboard.timeline.type.maintenance',
  ai_insight: 'dashboard.timeline.type.aiInsight',
};

/**
 * Placeholder titles the timeline API generates in English for events that
 * have no title of their own (src/services/timelineService.ts). They are
 * replaced by the translated event-type label and never shown as-is.
 */
const GENERATED_TITLES: ReadonlySet<string> = new Set([
  'AI Insight',
  'ICP Analysis',
  'Photo added',
  'Journal Entry',
  'Task completed',
]);

/** The API prefixes completed-task titles with this English text. */
const TASK_COMPLETED_PREFIX = 'Task completed: ';

/**
 * The user-authored part of an event title (journal title, task title, animal
 * name), or null when the API only sent a generated English placeholder.
 */
export function timelineEventOwnTitle(event: Pick<TimelineEvent, 'type' | 'title'>): string | null {
  const title = (event.title ?? '').trim();
  if (title === '' || GENERATED_TITLES.has(title)) return null;
  if (event.type === 'task' && title.startsWith(TASK_COMPLETED_PREFIX)) {
    const own = title.slice(TASK_COMPLETED_PREFIX.length).trim();
    return own === '' ? null : own;
  }
  return title;
}

/** Translated event-type label, e.g. "KI-Erkenntnis". */
export function timelineTypeLabel(type: TimelineEventType, t: TFunction): string {
  const key = Object.prototype.hasOwnProperty.call(TIMELINE_TYPE_LABEL_KEY, type)
    ? TIMELINE_TYPE_LABEL_KEY[type]
    : undefined;
  return key ? t(key) : type;
}

type TextEvent = Pick<TimelineEvent, 'type' | 'title'> & { subtype?: string | null };

/** Livestock events read "Neues Tier: <name>" / "Verstorben: <name>". */
function livestockLabel(event: TextEvent, t: TFunction): string | null {
  if (event.type !== 'livestock') return null;
  const name = timelineEventOwnTitle(event);
  if (name === null) return timelineTypeLabel(event.type, t);
  return event.subtype === 'deceased'
    ? t('dashboard.timeline.livestockDeceased', { name })
    : t('dashboard.timeline.livestockAdded', { name });
}

/** "Type label: own title", or just the type label for generated titles. */
export function timelineEventLabel(event: TextEvent, t: TFunction): string {
  const livestock = livestockLabel(event, t);
  if (livestock !== null) return livestock;
  const typeLabel = timelineTypeLabel(event.type, t);
  const own = timelineEventOwnTitle(event);
  return own === null ? typeLabel : `${typeLabel}: ${own}`;
}

/** Display title for a card: the own title, else the translated type label. */
export function timelineEventTitle(event: TextEvent, t: TFunction): string {
  return livestockLabel(event, t) ?? timelineEventOwnTitle(event) ?? timelineTypeLabel(event.type, t);
}

/** Anchor of the Reef Intelligence card on the dashboard (AI insight events link there). */
export const REEF_INTELLIGENCE_ANCHOR = 'reef-intelligence';

/**
 * Where an event opens. Every target is a route of the app router (App.tsx) — a
 * test checks that — so an event never leads to the "page not found" page.
 */
export function timelineEventHref(event: Pick<TimelineEvent, 'type' | 'sourceId'>, aquariumId: string): string {
  const aq = `/aquariums/${encodeURIComponent(aquariumId)}`;
  const id = encodeURIComponent(event.sourceId);
  switch (event.type) {
    case 'icp_analysis': return icpAnalysesPath(aquariumId, event.sourceId);
    case 'journal_entry': return `${aq}/journal/${id}`;
    case 'image': return `/images/${id}`;
    case 'task': return `${aq}/tasks`;
    case 'livestock': return `${aq}/livestock`;
    case 'ai_insight': return `/dashboard#${REEF_INTELLIGENCE_ANCHOR}`;
    // The measurement page's history view (#1641).
    case 'measurement': return `${aq}/measurements/new?view=history`;
    case 'dosing': return `${aq}/dosing`;
    default: return `${aq}/timeline`;
  }
}

/**
 * Relative time in the app language ("vor 5 Minuten", "gestern"); older
 * events get a localised date.
 */
export function formatTimelineRelativeTime(isoString: string, locale: string, now: Date = new Date()): string {
  const then = new Date(isoString);
  if (Number.isNaN(then.getTime())) return isoString;
  const diffSec = Math.round((then.getTime() - now.getTime()) / 1000);
  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });
  const absSec = Math.abs(diffSec);
  if (absSec < 60) return rtf.format(0, 'second');
  if (absSec < 3600) return rtf.format(Math.round(diffSec / 60), 'minute');
  if (absSec < 86400) return rtf.format(Math.round(diffSec / 3600), 'hour');

  const todayStart = new Date(now);
  todayStart.setHours(0, 0, 0, 0);
  const yesterdayStart = new Date(todayStart);
  yesterdayStart.setDate(yesterdayStart.getDate() - 1);
  if (then >= yesterdayStart && then < todayStart) return rtf.format(-1, 'day');

  return new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'short', year: 'numeric' }).format(then);
}
